import {
  SavingsPlanTransactionItemType,
  StakingTransactionItemType,
} from '@bts-web/core-features/transactions';
import {
  AllTransactionViewQueryData,
  CorporateActionViewQueryData,
  CryptoActionViewQueryData,
  CryptoTransfersTransactionsItemType,
  FiatTransactionViewQueryData,
  SwapOfferViewQueryData,
  TransactionViewQueryData,
} from '../types';
import {
  isCorporateActionTransaction,
  transactionTypeByTypeName,
} from '../../transactions/types/transactions';
import { CorporateActionType } from '@bts-web/data-layer/server';
import {
  getRoundedPrecision,
  intlAssetValueFormatting,
  intlFiatValueFormatting,
  PrecisionFloat,
  timeScalarOrNull,
  type TimeScalar,
} from '@bts-web/utils-formatting';

export function isCryptoTransferTransaction(
  transaction: AllTransactionViewQueryData,
): transaction is CryptoTransfersTransactionsItemType {
  return (
    transaction !== null &&
    (transaction?.__typename === 'DepositCryptoTransfersTransaction' ||
      transaction?.__typename === 'WithdrawalCryptoTransfersTransaction')
  );
}

export function isBuyOrSellTransaction(
  transaction: AllTransactionViewQueryData,
): transaction is TransactionViewQueryData {
  return (
    transaction?.__typename === 'BuyTransaction' ||
    transaction?.__typename === 'SellTransaction'
  );
}

export function isStakingActionViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is StakingTransactionItemType & {
  __typename: 'StakeStakingTransaction';
} {
  return transaction?.__typename === 'StakeStakingTransaction';
}

export function isStakingRewardActionViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is StakingTransactionItemType & {
  __typename: 'StakingRewardTransaction';
} {
  return transaction?.__typename === 'StakingRewardTransaction';
}

export function isUnstakingActionViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is StakingTransactionItemType & {
  __typename: 'UnstakeStakingTransaction';
} {
  return transaction?.__typename === 'UnstakeStakingTransaction';
}

export function isCryptoActionViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is CryptoActionViewQueryData {
  return transaction?.__typename === 'MergerCryptoTransaction';
}

//remove comments when using
// export function isGiveawayActionViewQueryNode(
//   transaction: AllTransactionViewQueryData,
// ): transaction is Extract<
//   CryptoActionViewQueryData,
//   { __typename: 'GiveawayTransaction' }
// > {
//   return transaction?.__typename === 'GiveawayTransaction';
// }

export function isCorporateActionViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is CorporateActionViewQueryData {
  return (
    transaction !== null &&
    isCorporateActionTransaction(
      transactionTypeByTypeName[
        transaction?.__typename as CorporateActionType as keyof typeof transactionTypeByTypeName
      ],
    )
  );
}

export function isNUITransactionCategory(
  transaction: AllTransactionViewQueryData,
): transaction is TransactionViewQueryData {
  return (
    (transaction as TransactionViewQueryData)?.category === 'NON_USER_INITIATED'
  );
}

export function isSavingsPlanItemType(
  transaction: AllTransactionViewQueryData,
): transaction is CryptoTransfersTransactionsItemType {
  return transaction?.__typename === 'SavingsPlanTransaction';
}

export function isInternalSavingsPlanItemType(
  transaction: AllTransactionViewQueryData,
): transaction is SavingsPlanTransactionItemType {
  return transaction?.__typename === 'InternalSavingsPlanTransaction';
}

export function isSwapViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is SwapOfferViewQueryData {
  return transaction?.__typename === 'SwapTransaction';
}

export function isFiatTransactionViewQueryNode(
  transaction: AllTransactionViewQueryData,
): transaction is FiatTransactionViewQueryData {
  return transaction?.__typename === 'FiatTransaction';
}

export const getTransactionTimeScalar = (
  transaction: AllTransactionViewQueryData,
): TimeScalar | null => {
  if (transaction && 'createdAt' in transaction) {
    return timeScalarOrNull(transaction.createdAt);
  }

  if (transaction && 'time' in transaction) {
    return timeScalarOrNull(transaction.time);
  }

  return null;
};

export const assetAmountFormat = (
  assetSymbol: string | null | undefined,
  assetAmount: PrecisionFloat | null | undefined,
  locale: string,
): string | null => {
  if (!assetAmount || !assetSymbol) return null;

  const formattedValue = intlAssetValueFormatting(
    Number(getRoundedPrecision(assetAmount)),
    {
      locale,
      fractionDigits: assetAmount.precision ?? 8,
    },
  );

  return `${assetSymbol} ${formattedValue}`;
};

export const fiatAmountFormat = (
  amount: PrecisionFloat | null | undefined,
  locale: string,
  currency?: string,
): string | null => {
  if (!amount) return null;

  return intlFiatValueFormatting(getRoundedPrecision(amount), {
    locale,
    currency,
    fractionDigits: amount.precision,
  });
};
